import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"] as const;

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_INPUT_EDGE = 4096;
export const WARN_INPUT_EDGE = 2048;

export type ScaleOption = 2 | 4 | 8;
export type UpscaleMode = "photo" | "anime";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

export function formatPixels(width: number, height: number): string {
  return `${width.toLocaleString()} × ${height.toLocaleString()}`;
}

export interface FileValidation {
  ok: boolean;
  error?: string;
}

export function validateImageFile(file: File): FileValidation {
  if (!file) {
    return { ok: false, error: "No file selected." };
  }

  const typeOk =
    ACCEPTED_TYPES.includes(file.type as (typeof ACCEPTED_TYPES)[number]) ||
    ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

  if (!typeOk) {
    return {
      ok: false,
      error: "Unsupported format. Use JPG, PNG, or WEBP.",
    };
  }

  if (file.size > MAX_FILE_BYTES) {
    return {
      ok: false,
      error: `File is ${formatBytes(file.size)}. Maximum is ${formatBytes(MAX_FILE_BYTES)}.`,
    };
  }

  if (file.size < 32) {
    return { ok: false, error: "File is empty or corrupted." };
  }

  return { ok: true };
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Unexpected file reader result."));
    };
    reader.readAsDataURL(file);
  });
}

export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not decode image."));
    image.src = src;
  });
}

export async function readImageMeta(
  file: File,
): Promise<{ width: number; height: number; src: string }> {
  const src = URL.createObjectURL(file);
  try {
    const image = await loadImageElement(src);
    return { width: image.naturalWidth, height: image.naturalHeight, src };
  } catch (error) {
    URL.revokeObjectURL(src);
    throw error;
  }
}

export function estimateOutputSize(
  width: number,
  height: number,
  scale: ScaleOption,
): { width: number; height: number; megapixels: number } {
  const outW = width * scale;
  const outH = height * scale;
  return {
    width: outW,
    height: outH,
    megapixels: (outW * outH) / 1_000_000,
  };
}

export function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1500);
}

export async function copyBlobToClipboard(blob: Blob): Promise<void> {
  if (!("clipboard" in navigator) || typeof ClipboardItem === "undefined") {
    throw new Error("Clipboard images are not supported in this browser.");
  }

  const type = blob.type === "image/png" ? "image/png" : "image/png";
  const payload =
    blob.type === "image/png" ? blob : await transcodeToPng(blob);

  await navigator.clipboard.write([
    new ClipboardItem({ [type]: payload }),
  ]);
}

export async function transcodeToPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable.");
  ctx.drawImage(bitmap, 0, 0);
  const png = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("PNG encode failed."))),
      "image/png",
    );
  });
  bitmap.close();
  return png;
}

export function extensionForType(type: string): string {
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  if (type.includes("webp")) return "webp";
  return "png";
}

export function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function nowStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
